/* eslint-disable no-unused-expressions */
import React, { useEffect, useMemo, useState } from 'react';
import * as PropTypes from 'prop-types';
import Markdown from 'markdown-to-jsx';
import NewsfeedButton from '../Newsfeed/Button';
import Tiptap from '../Tiptap/Tiptap';

const LearnArticleContentModalView = ({
                                          posting,
                                          languages = '[]',
                                          articleLanguages = '[]',
                                          onSubmit = () => {}
                                      }) => {
    const languagesList = useMemo(() => { try { return JSON.parse(languages || '[]'); } catch { return []; } }, [languages]);
    const rows = useMemo(() => { try { return JSON.parse(articleLanguages || '[]'); } catch { return []; } }, [articleLanguages]);

    const [languageCode, setLanguageCode] = useState('');
    const [title, setTitle] = useState('');
    const [shortDescription, setShortDescription] = useState('');
    const [metaDescription, setMetaDescription] = useState('');
    const [metaKeywords, setMetaKeywords] = useState('');
    const [content, setContent] = useState('');
    const [isEditable, setEditable] = useState(true);

    const byCode = code => rows.find(r => r?.languageCode === code) || null;

    const hideModal = () => {
        const modalWrapper = document.querySelector('.modal-wrapper--content');
        if (modalWrapper) modalWrapper.classList.toggle('hidden');
    };

    useEffect(() => {
        const first = rows[0]?.languageCode || languagesList[0]?.languageCode || '';
        setLanguageCode(first || '');
    }, [rows.length, languagesList.length]);

    useEffect(() => {
        if (!languageCode) return;
        const row = byCode(languageCode) || {};
        setTitle(row.title || '');
        setShortDescription(row.shortDescription || '');
        setMetaDescription(row.metaDescription || '');
        setMetaKeywords(row.metaKeywords || '');
        setContent(row.content || '');
        setEditable(!(row?.content?.length > 0));
    }, [languageCode]);

    const handleFormSubmit = e => {
        e.preventDefault();
        onSubmit({
            languageCode,
            title: (title || '').trim() || null,
            shortDescription: (shortDescription || '').trim() || null,
            metaDescription: (metaDescription || '').trim() || null,
            metaKeywords: (metaKeywords || '').trim() || null,
            content: content || ''
        });
    };

    return (
        <div
            tabIndex="-1"
            className="overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 w-full md:inset-0 h-full md:h-screen justify-center items-center flex bg-black/90 md:pb-12 pb-14"
            role="dialog"
        >
            <div className="bg-white rounded-2xl py-6 md:w-1/2 xl:w-2/5 w-11/12 max-w-full h-auto m-auto md:mt-12 mt-14 flex flex-col gap-4">
                <div className="flex items-center justify-between px-6">
                    <h2 className="text-gray-800 font-semibold md:text-xl text-lg leading-8 mb-0">Edit article content</h2>
                    <button type="button" onClick={hideModal} className="inline-flex items-center justify-center w-6 h-6">
                        <span className="material-icons text-gray-600">close</span>
                    </button>
                </div>

                <div className="w-full py-2 md:px-6 px-4 flex items-start gap-6">
                    <form onSubmit={handleFormSubmit} className="flex w-full flex-col gap-3">
                        <label htmlFor="language" className="font-semibold text-gray-800 text-lg">
                            Language
                            <select
                                id="language"
                                className="mt-2 mb-2 w-full bg-white border border-gray-300 focus:border-blue-600 focus:outline-hidden text-gray-800 text-base leading-6 rounded p-2.5 appearance-none cursor-pointer"
                                onChange={e => setLanguageCode(e.target.value)}
                                value={languageCode || ''}
                                required
                            >
                                <option value="">— Select language —</option>
                                {languagesList.map(l => (
                                    <option key={l?.languageCode} value={l?.languageCode}>
                                        {l?.name} ({l?.languageCode})
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label htmlFor="title" className="font-semibold text-gray-800 text-lg">
                            Title
                            <input
                                id="title"
                                type="text"
                                className="text-left p-3 border w-full border-gray-300 placeholder-gray-200 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500"
                                onChange={e => setTitle(e.target.value)}
                                value={title || ''}
                            />
                        </label>

                        <label htmlFor="shortDescription" className="font-semibold text-gray-800 text-lg">
                            Short Description
                            <textarea
                                id="shortDescription"
                                rows="3"
                                className="text-left p-3 border w-full border-gray-300 placeholder-gray-200 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500"
                                onChange={e => setShortDescription(e.target.value)}
                                value={shortDescription || ''}
                            />
                        </label>

                        <label htmlFor="metaDescription" className="font-semibold text-gray-800 text-lg">
                            Meta Description
                            <textarea
                                id="metaDescription"
                                rows="3"
                                className="text-left p-3 border w-full border-gray-300 placeholder-gray-200 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500"
                                onChange={e => setMetaDescription(e.target.value)}
                                value={metaDescription || ''}
                            />
                        </label>

                        <label htmlFor="metaKeywords" className="font-semibold text-gray-800 text-lg">
                            Meta Keywords
                            <input
                                id="metaKeywords"
                                type="text"
                                className="text-left p-3 border w-full border-gray-300 placeholder-gray-200 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500"
                                onChange={e => setMetaKeywords(e.target.value)}
                                value={metaKeywords || ''}
                            />
                        </label>

                        <span className="font-semibold text-gray-800 text-lg">Content</span>
                        <div id="content" tabIndex={-1}>
                            {rows.length ? (
                                isEditable ? (
                                    <Tiptap content={content || ''} updateContent={v => setContent(v)} />
                                ) : (
                                    <div className="relative w-full gap-2 py-2 rounded-sm border border-gray-300 p-3 max-h-96 overflow-auto custom-thumb">
                                        <button
                                            type="button"
                                            className="sticky top-3 px-2 py-1 bg-gray-200 text-gray-800 font-semibold text-base rounded-md material-symbols-outlined float-right shadow-md"
                                            onClick={() => setEditable(v => !v)}
                                        >
                                            edit
                                        </button>
                                        {content && (
                                            <Markdown options={{ forceBlock: true }} className="prose">
                                                {content.length > 800 ? `${content.slice(0, 800)}...` : content}
                                            </Markdown>
                                        )}
                                    </div>
                                )
                            ) : (
                                <Tiptap content={content || ''} updateContent={v => setContent(v)} />
                            )}
                        </div>

                        <div className="w-full flex items-center justify-end">
                            <NewsfeedButton classNames="md:w-auto w-full" loading={posting} title="Save" type="primary" />
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default LearnArticleContentModalView;

LearnArticleContentModalView.propTypes = {
    posting: PropTypes.bool.isRequired,
    languages: PropTypes.string,
    articleLanguages: PropTypes.string,
    onSubmit: PropTypes.func
};
